import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import { format } from 'date-fns';
import { checkComponentPermission } from '../../../utils/share';
import Iconify from '../../../components/iconify';
import TablePaginationCustom from '../../../components/table/table-pagination-custom';
import CompOffStatusBadge from './CompOffStatusBadge';
import { COMP_OFF_STATUS } from '../constants';

const CompOffTable = ({
  compOffRequests,
  onCancelCompOff,
  onEditCompOff,
  showEmployee = false,
  currentUserId,
  count = 0,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd-MMM-yyyy');
    } catch (error) {
      return dateString;
    }
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {showEmployee && <TableCell>Employee</TableCell>}
              <TableCell>Work Date</TableCell>
              <TableCell>Hours Worked</TableCell>
              <TableCell>Credit Days</TableCell>
              <TableCell>Status</TableCell>
              {showEmployee && <TableCell>Reviewed By</TableCell>}
              {showEmployee && <TableCell>Reviewed Date</TableCell>}
              <TableCell sx={{ pl: 2 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {compOffRequests && compOffRequests.length > 0 ? (
              compOffRequests.map((compOff) => (
                <TableRow key={compOff.id} hover>
                  {showEmployee && (
                    <TableCell>{compOff.employee?.userName || compOff.employee?.email || '-'}</TableCell>
                  )}
                  <TableCell>{formatDate(compOff.workDate)}</TableCell>
                  <TableCell>{compOff.hoursWorked}h</TableCell>
                  <TableCell>{compOff.creditDays != null ? `${compOff.creditDays}d` : '-'}</TableCell>
                  <TableCell>
                    <CompOffStatusBadge status={compOff.status} />
                  </TableCell>
                  {showEmployee && (
                    <TableCell>
                      {compOff.approver && compOff.approver.id === currentUserId
                        ? 'Me'
                        : compOff.approver?.userName || compOff.approver?.email || '-'}
                    </TableCell>
                  )}
                  {showEmployee && <TableCell>{formatDate(compOff.reviewedAt)}</TableCell>}
                  <TableCell align="left">
                    <Stack direction="row" spacing={1} justifyContent="left">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/comp-off/${compOff.id}`);
                          }}
                        >
                          <Iconify icon="eva:eye-fill" />
                        </IconButton>
                      </Tooltip>
                      {!showEmployee && compOff.status === COMP_OFF_STATUS.PENDING && compOff.userId === currentUserId && (
                        <>
                          {checkComponentPermission('UPDATE_COMP_OFF') && (
                            <Tooltip title="Edit Comp-Off">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditCompOff(compOff.id);
                                }}
                              >
                                <Iconify icon="eva:edit-fill" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {checkComponentPermission('DELETE_COMP_OFF') && (
                            <Tooltip title="Delete Comp-Off">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onCancelCompOff(compOff.id);
                                }}
                              >
                                <Iconify icon="material-symbols:delete" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={showEmployee ? 8 : 5} align="center">
                  No comp-off requests found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {count > 0 && (
        <TablePaginationCustom
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      )}
    </>
  );
};

CompOffTable.propTypes = {
  compOffRequests: PropTypes.array.isRequired,
  onCancelCompOff: PropTypes.func,
  onEditCompOff: PropTypes.func,
  showEmployee: PropTypes.bool,
  currentUserId: PropTypes.number,
  count: PropTypes.number,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
};

export default CompOffTable;
